<template>
    <PanelPage>
        <template v-slot:title>
            <SmartBreadcrumbs
                type="InertiaLink"
                :pages="[
                    {
                        name: $tc('messages.gamification_sets', 2),
                        href: meta.links.set_index,
                    },
                    {
                        name: gamificationSet.breadcrumb_title,
                        href: meta.links.set_show,
                    },
                    {
                        name: $tc('messages.itineraries', 2),
                        href: meta.links.itineraries_index,
                    },
                    {
                        name: itinerary.name,
                        href: meta.links.itineraries_show,
                    },
                    {
                        name: $tc('messages.itinerary_stations', 2),
                        href: null,
                    },
                    {
                        name: $t('messages.create'),
                        href: null,
                    },
                ]"
            />
        </template>

        <form :action="meta.links.update" method="post" @submit.prevent="submit">
            <TitleFieldsFormSection>
                <template v-slot:title>
                    {{ $t('messages.information') }}
                </template>
                <div class="md:grid md:grid-cols-12 md:gap-6">
                    <div class="sm:col-span-8">
                        <div class="mt-1">
                            <FormLabel :value="$t('messages.name')" for="name" />
                            <FormInput
                                id="name"
                                v-model="form.name"
                                name="name"
                                type="text"
                                maxlength="191"
                                class="mt-1 w-full"
                            />
                            <FormError :value="form.errors.name" class="mt-2" />
                        </div>
                    </div>

                    <div class="sm:col-span-4">
                        <div class="mt-1">
                            <FormLabel :value="$t('messages.unlocked_at')" for="unlocked_at" />
                            <FormInput
                                id="unlocked_at"
                                v-model="form.unlocked_at"
                                name="unlocked_at"
                                type="date"
                                class="mt-1"
                            />
                            <FormError :value="form.errors.unlocked_at" class="mt-2" />
                        </div>
                    </div>

                    <div class="sm:col-span-12">
                        <div class="mt-1">
                            <FormLabel :value="$t('messages.description')" for="description" />
                            <Editor id="description" v-model="form.description" name="description" class="mt-2" />
                            <FormError :value="form.errors.description" class="mt-2" />
                        </div>
                    </div>

                    <div class="sm:col-span-12" v-if="type === 'url'">
                        <div class="mt-1">
                            <FormLabel :value="$t('messages.url')" for="url" />
                            <FormInput
                                id="url"
                                v-model="form.url"
                                name="url"
                                type="text"
                                maxlength="191"
                                class="mt-1 w-full"
                            />
                            <FormError :value="form.errors.url" class="mt-2" />
                        </div>
                    </div>

                    <div class="sm:col-span-6" v-if="type === 'gamification_chest'">
                        <div class="mt-1">
                            <FormLabel :value="$tc('messages.gamification_chests', 1)" for="gamification_chests" />
                            <TreasureChest class="h-40" :type="form.settings.type" :is-open="true" />
                            <FormSelect
                                required
                                id="gamification_chests"
                                v-model="form.settings.type"
                                :options="options.types"
                                class="mt-1 w-full"
                                :null-option="false"
                            />
                        </div>
                    </div>
                    <div class="sm:col-span-6" v-if="type === 'final'">
                        <div class="mt-1">
                            <FormLabel :value="$tc('messages.images', 1)" for="type" />
                            <div class="mt-1 flex gap-x-2 items-center">
                                <label v-for="type in options.types" :key="type.value">
                                    <div
                                        class="rounded-lg"
                                        :class="type.value === form.settings.type ? 'bg-indigo-300' : 'bg-gray-100'"
                                    >
                                        <input
                                            type="radio"
                                            name="type"
                                            class="hidden"
                                            :id="type.value"
                                            :value="type.value"
                                            v-model="form.settings.type"
                                        />
                                        <img :src="type.url" alt="" class="p-4 h-40 w-40 object-contain" />
                                    </div>
                                </label>
                            </div>
                        </div>
                    </div>
                </div>
                <div class="" v-if="usesActivity">
                    <StationActivitySelector
                        :options="options"
                        :defaults="defaults"
                        v-model="form.activity_id"
                        :url="meta.links.station_activity_search"
                        @subject-id="setSubjectId"
                    />
                    <FormError :value="form.errors.activity_id" class="mt-2" />
                </div>
            </TitleFieldsFormSection>

            <StationProgrammingSessionSelector
                v-if="meta.info.has_programmings && form.activity_id"
                :subject-id="subject_id"
                :programmings-search-url="meta.links.station_programming_search"
                :programmings-session-search-url="meta.links.station_programming_session_search"
                v-model="form.session_id"
            />
            <TitleFieldsFormSection>
                <template v-slot:title>
                    <div>
                        {{ $tc('messages.students', 2) }}
                    </div>
                    <div v-if="form.all_students === false">
                        <AlertMessage class="mt-5 inline-block" type="info">
                            <template v-slot:title>
                                {{ $t('messages.information') }}
                            </template>
                            <template v-slot:body>
                                {{ $t('messages.station_visible_only_by_selected_students') }}
                            </template>
                        </AlertMessage>
                    </div>
                </template>
                <div class="mt-1">
                    <FormLabel :value="$t('messages.all_students')" for="all_students" />
                    <input
                        id="all_students"
                        type="checkbox"
                        v-model="form.all_students"
                        name="all_students"
                        class="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300 rounded"
                    />
                    <FormError :value="form.errors.all_students" class="mt-2" />
                </div>
                <div class="mt-5 md:mt-0 bg-gray-50 md:col-span-2" v-if="form.all_students === false">
                    <StudentBatchSelector :groups="groups" v-model="form.student_ids" />
                </div>
            </TitleFieldsFormSection>

            <div class="flex justify-end mt-5">
                <SubmitFormButton :button-text="$t('messages.save')" :processing="form.processing" />
            </div>
        </form>
    </PanelPage>
</template>

<script>
import Layout from '@/Shared/Layout.vue';
import PanelPage from '@/Shared/PanelPage.vue';
import TitleFieldsFormSection from '@/components/TitleFieldsFormSection.vue';
import FormInput from '@/components/Forms/FormInput.vue';
import FormLabel from '@/components/Forms/FormLabel.vue';
import FormError from '@/components/Forms/FormError.vue';
import SubmitFormButton from '@/Pages/Teacher/Activities/Subactivities/SubmitFormButton.vue';
import StationActivitySelector from '@/components/pageSpecific/StationActivitySelector.vue';
import StationProgrammingSessionSelector from '@/components/pageSpecific/StationProgrammingSessionSelector.vue';
import TreasureChest from '@/components/TreasureChest.vue';
import FormSelect from '@/components/Forms/FormSelect.vue';
import Editor from '@/components/Forms/RichTextEditor/Editor.vue';
import StudentBatchSelector from '@/components/pageSpecific/StudentBatchSelector.vue';
import AlertMessage from '@/components/AlertMessage.vue';

export default {
    components: {
        AlertMessage,
        StudentBatchSelector,
        Editor,
        FormSelect,
        TreasureChest,
        StationProgrammingSessionSelector,
        StationActivitySelector,
        PanelPage,
        TitleFieldsFormSection,
        FormInput,
        FormLabel,
        FormError,
        SubmitFormButton,
    },
    layout: Layout,
    name: 'Create',
    props: ['meta', 'options', 'gamificationSet', 'itinerary', 'type', 'groups', 'defaults'],
    data() {
        return {
            form: this.$inertia.form({
                name: '',
                description: '',
                type: this.type,
                unlocked_at: null,
                activity_id: null,
                session_id: null,
                url: '',
                settings: this.defaults.settings,
                all_students: true,
                student_ids: [],
            }),
            subject_id: null,
            programmings: [],
        };
    },
    methods: {
        submit() {
            this.form.post(this.meta.links.store);
        },
        setSubjectId(subjectId) {
            this.subject_id = subjectId;
        },
    },
    computed: {
        usesActivity: function () {
            return ['quiz', 'rubric'].includes(this.type);
        },
    },
};
</script>
